/**
 * Active Intelligence Biases
 * ---------------------------------------------------------------
 * Operator-grade panel exposing the live `generation_biases` set
 * committed by the Adaptive Learning Engine. These are the
 * directives currently steering Generate Intelligence Batch.
 *
 * Forensic dark/cyan/amber language. Live-refreshing.
 */

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowUpRight, ArrowDownRight, Cpu, Loader2, Activity,
} from "lucide-react";

interface ActiveBiasRow {
  id: string;
  bias_direction: "increase" | "reduce";
  dimension: string;
  key: string;
  rationale: string | null;
  confidence: number;
  weight: number;
  impact_score: number;
  cycle_id: string | null;
  updated_at: string;
}

interface CycleSummary {
  id: string;
  cycle_number: number;
  committed_at: string;
}

const titleCase = (s: string) =>
  (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const Bar = ({ value, tone = "cyan" }: { value: number; tone?: "cyan" | "amber" | "emerald" | "red" }) => {
  const map: Record<string, string> = {
    cyan: "bg-cyan", amber: "bg-amber-400", emerald: "bg-emerald-400", red: "bg-red-400",
  };
  return (
    <div className="h-1 w-full rounded-full bg-charcoal-deep border border-glass-border overflow-hidden">
      <div className={`h-full ${map[tone]} transition-all duration-700`}
        style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
};

export default function ActiveIntelligenceBiases() {
  const [biases, setBiases] = useState<ActiveBiasRow[]>([]);
  const [cycles, setCycles] = useState<Map<string, CycleSummary>>(new Map());
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: b } = await (supabase as any)
      .from("generation_biases")
      .select("id, bias_direction, dimension, key, rationale, confidence, weight, impact_score, cycle_id, updated_at")
      .eq("active", true)
      .order("confidence", { ascending: false })
      .limit(40);
    const rows = (b ?? []) as ActiveBiasRow[];
    setBiases(rows);

    const cycleIds = Array.from(new Set(rows.map((r) => r.cycle_id).filter(Boolean))) as string[];
    if (cycleIds.length > 0) {
      const { data: c } = await (supabase as any)
        .from("learning_cycles")
        .select("id, cycle_number, committed_at")
        .in("id", cycleIds);
      const m = new Map<string, CycleSummary>();
      (c ?? []).forEach((row: CycleSummary) => m.set(row.id, row));
      setCycles(m);
    } else {
      setCycles(new Map());
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Live refresh on commit + periodic
  useEffect(() => {
    const onCommit = () => { void load(); };
    window.addEventListener("intelligence-committed", onCommit);
    const id = window.setInterval(() => { void load(); }, 30_000);
    return () => {
      window.removeEventListener("intelligence-committed", onCommit);
      window.clearInterval(id);
    };
  }, [load]);

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm relative overflow-hidden">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan/60 to-transparent animate-pulse" />
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Cpu className="w-4 h-4 text-cyan animate-pulse" />
            Active Intelligence Biases
            <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
              Live generation modifiers
            </span>
          </CardTitle>
          <Badge variant="outline" className="border-cyan/40 text-cyan font-mono text-[10px]">
            <Activity className="w-3 h-3 mr-1" />
            {biases.length} ACTIVE
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-cyan" />
          </div>
        ) : biases.length === 0 ? (
          <div className="px-6 py-10 text-center text-[11px] uppercase tracking-wider font-mono text-muted-foreground">
            No active directives · commit intelligence in Admin → Intelligence to engage
          </div>
        ) : (
          <ScrollArea className="max-h-[420px]">
            <div className="divide-y divide-glass-border">
              {biases.map((b) => {
                const Icon = b.bias_direction === "increase" ? ArrowUpRight : ArrowDownRight;
                const tone = b.bias_direction === "increase"
                  ? "text-emerald-300 border-emerald-400/40"
                  : "text-red-300 border-red-400/40";
                const cycle = b.cycle_id ? cycles.get(b.cycle_id) : null;
                return (
                  <div key={b.id} className="px-4 py-3 flex items-start gap-3">
                    <Icon className={`w-4 h-4 mt-0.5 ${b.bias_direction === "increase" ? "text-emerald-300" : "text-red-300"}`} />
                    <div className="flex-1 min-w-0 space-y-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge variant="outline" className={`text-[9px] font-mono uppercase tracking-wider ${tone}`}>
                          {b.bias_direction}
                        </Badge>
                        <span className="text-sm uppercase tracking-wider font-semibold text-foreground">
                          {titleCase(b.dimension)} · {titleCase(b.key)}
                        </span>
                        <span className="text-[10px] font-mono text-cyan ml-auto">
                          w {b.weight.toFixed(2)}
                        </span>
                      </div>
                      <div className="text-[11px] text-muted-foreground font-mono italic">
                        {b.rationale ?? "Auto-derived behavioral directive"}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-1">
                        <div>
                          <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-muted-foreground font-mono mb-0.5">
                            <span>Confidence</span><span className="text-foreground">{Math.round(b.confidence)}%</span>
                          </div>
                          <Bar value={b.confidence} tone="cyan" />
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-[9px] uppercase tracking-wider text-muted-foreground font-mono mb-0.5">
                            <span>Impact</span><span className="text-foreground">{Math.round(b.impact_score)}</span>
                          </div>
                          <Bar value={b.impact_score} tone={b.bias_direction === "increase" ? "emerald" : "amber"} />
                        </div>
                      </div>
                      <div className="flex items-center gap-3 pt-1 text-[9px] uppercase tracking-[0.18em] font-mono text-muted-foreground/80">
                        <span>Cycle {cycle?.cycle_number ?? "—"}</span>
                        <span>•</span>
                        <span>
                          Committed {cycle?.committed_at ? new Date(cycle.committed_at).toLocaleString() : new Date(b.updated_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
